import React, { useState, useRef, useEffect } from 'react';
import {
  Send as SendIcon,
  User as UserIcon,
  Bot as BotIcon,
  AlertCircle as AlertCircleIcon,
  ChevronDown as ChevronDownIcon,
  Clock as ClockIcon,
  Zap as ZapIcon,
  RotateCcw as RotateCcwIcon,
  Loader2 as LoaderIcon,
  FileText as FileTextIcon
} from 'lucide-react';
import { useSettings } from '../SettingsContext';
import FileUpload from './FileUpload';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const agentColors = {
  gpt: {
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    accent: 'emerald'
  },
  claude: {
    border: 'border-orange-500/30',
    bg: 'bg-orange-500/10',
    text: 'text-orange-400',
    accent: 'orange'
  },
  gemini: {
    border: 'border-blue-500/30',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    accent: 'blue'
  }
};

const agentNames = {
  gpt: 'GPT',
  claude: 'Claude',
  gemini: 'Gemini'
};

const SoloChat = ({
  token,
  thread,
  loading,
  onSendMessage,
  onNewThread,
  onSummarize,
  summarizing,
  promptsRemaining
}) => {
  const { settings, setSoloAgent } = useSettings();
  const [prompt, setPrompt] = useState('');
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedAgent = settings.soloAgent || 'gpt';

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAgentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      // Reset to auto to get true content height
      textareaRef.current.style.height = 'auto';
      // Get the actual scroll height of content
      const scrollHeight = textareaRef.current.scrollHeight;
      // Apply height with min/max constraints
      textareaRef.current.style.height = `${Math.max(48, Math.min(scrollHeight, 200))}px`;
    }
  }, [prompt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || sending) return;

    setSending(true);
    try {
      await onSendMessage(prompt.trim(), selectedAgent, files);
      setPrompt('');
      setFiles([]);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const renderMessageContent = (content) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={oneDark}
                language={match[1]}
                PreTag="div"
                className="rounded-lg my-2"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className="bg-slate-800 px-1.5 py-0.5 rounded text-cyan-300 text-sm" {...props}>
                {children}
              </code>
            );
          },
          p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="text-slate-300">{children}</li>,
          h1: ({ children }) => <h1 className="text-xl font-bold text-white mb-3">{children}</h1>,
          h2: ({ children }) => <h2 className="text-lg font-bold text-white mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-bold text-white mb-2">{children}</h3>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-cyan-500 pl-4 italic text-slate-400 my-3">{children}</blockquote>
          ),
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">{children}</a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    );
  };

  // Check if thread is near or at message limit
  const messageLimit = 100; // Should match backend limit
  const isNearLimit = thread?.messages?.length >= messageLimit - 10;
  const isAtLimit = thread?.messages?.length >= messageLimit;
  const canSummarize = thread?.messages?.length > 0 && promptsRemaining >= 1 && !loading && !sending && !summarizing;

  return (
    <div className="flex flex-col h-full">
      {/* Summarize Button - Shows when thread has messages */}
      {thread?.messages?.length > 0 && (
        <div className="flex-shrink-0 px-4 pt-3 pb-1 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isAtLimit && (
              <span className="text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded-lg border border-red-500/30">
                Thread limit reached ({messageLimit} messages)
              </span>
            )}
            {isNearLimit && !isAtLimit && (
              <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-lg border border-amber-500/30">
                {messageLimit - thread.messages.length} messages left
              </span>
            )}
          </div>
          <button
            onClick={onSummarize}
            disabled={!canSummarize}
            className={`group flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              canSummarize
                ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20 hover:border-purple-500/50'
                : 'bg-slate-800/50 text-slate-500 border border-slate-700/30 cursor-not-allowed'
            }`}
            title={
              !canSummarize
                ? promptsRemaining < 1
                  ? 'No prompts remaining'
                  : 'Cannot summarize right now'
                : 'Summarize conversation and continue in a new thread (1 prompt)'
            }
          >
            {summarizing ? (
              <LoaderIcon className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileTextIcon className="h-3.5 w-3.5" />
            )}
            <span className="hidden sm:inline">
              {summarizing ? 'Summarizing...' : 'Summarize & Continue'}
            </span>
            <span className="sm:hidden">
              {summarizing ? '...' : 'Summary'}
            </span>
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!thread || thread.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <BotIcon className="h-8 w-8 text-slate-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Solo Mode</h3>
              <p className="text-slate-400 mb-4">
                Chat with one AI at a time. Switch between GPT, Claude, and Gemini
                at any point while keeping your conversation context.
              </p>
              <div className="flex justify-center gap-2">
                {['gpt', 'claude', 'gemini'].map(agent => (
                  <button
                    key={agent}
                    onClick={() => setSoloAgent(agent)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedAgent === agent
                        ? `${agentColors[agent].bg} ${agentColors[agent].text} ${agentColors[agent].border} border`
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    {agentNames[agent]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {thread.messages.map((message, index) => (
              <div key={message.id || index} className="space-y-3">
                {/* User Message */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 glass rounded-xl p-4 border border-slate-700/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-cyan-300">You</span>
                      <span className="text-xs text-slate-500">
                        {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
                      </span>
                    </div>
                    <p className="text-white">{message.prompt}</p>
                  </div>
                </div>

                {/* AI Response */}
                {message.soloResponse && (
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${agentColors[message.soloResponse.agent]?.bg || 'bg-slate-700'} flex items-center justify-center`}>
                      <BotIcon className={`h-4 w-4 ${agentColors[message.soloResponse.agent]?.text || 'text-slate-400'}`} />
                    </div>
                    <div className={`flex-1 glass rounded-xl p-4 border ${agentColors[message.soloResponse.agent]?.border || 'border-slate-700/50'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${agentColors[message.soloResponse.agent]?.text || 'text-white'}`}>
                            {message.soloResponse.model || agentNames[message.soloResponse.agent]}
                          </span>
                          {message.soloResponse.fallback && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300" title={message.soloResponse.fallbackError}>
                              Fallback
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          {message.soloResponse.responseTime && (
                            <span className="flex items-center gap-1">
                              <ClockIcon className="h-3 w-3" />
                              {(message.soloResponse.responseTime / 1000).toFixed(1)}s
                            </span>
                          )}
                          {message.soloResponse.score > 0 && (
                            <span className="flex items-center gap-1 text-yellow-400">
                              <ZapIcon className="h-3 w-3" />
                              {Math.round(message.soloResponse.score)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Fallback Notice */}
                      {message.soloResponse.fallback && (
                        <div className="mb-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                          <div className="flex items-start gap-2">
                            <AlertCircleIcon className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-amber-300">
                              <strong>{agentNames[message.soloResponse.originalAgent]}</strong> failed to respond.
                              {message.soloResponse.fallbackError && (
                                <span className="text-amber-400/70"> Error: {message.soloResponse.fallbackError}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="text-slate-200 prose prose-invert prose-sm max-w-none">
                        {renderMessageContent(message.soloResponse.content)}
                      </div>

                      {/* Token Info */}
                      {message.soloResponse.tokens && (
                        <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center gap-4 text-xs text-slate-500">
                          <span>Input: {message.soloResponse.tokens.input?.toLocaleString() || 0}</span>
                          <span>Output: {message.soloResponse.tokens.output?.toLocaleString() || 0}</span>
                          <span>Total: {message.soloResponse.tokens.total?.toLocaleString() || 0}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}

        {loading && (
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${agentColors[selectedAgent].bg} flex items-center justify-center`}>
              <LoaderIcon className={`h-4 w-4 ${agentColors[selectedAgent].text} animate-spin`} />
            </div>
            <div className={`glass rounded-xl p-4 border ${agentColors[selectedAgent].border}`}>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${agentColors[selectedAgent].text}`}>
                  {agentNames[selectedAgent]} is thinking...
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-slate-700/50 p-4 bg-slate-900/80 backdrop-blur-sm">
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* File Upload */}
          <FileUpload files={files} onFilesChange={setFiles} disabled={sending || loading} />

          <div className="flex items-end gap-3">
            {/* Agent Selector */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setAgentDropdownOpen(!agentDropdownOpen)}
                className={`h-12 px-4 rounded-xl border flex items-center gap-2 transition-all ${
                  agentColors[selectedAgent].border
                } ${agentColors[selectedAgent].bg} hover:opacity-80`}
              >
                <BotIcon className={`h-4 w-4 ${agentColors[selectedAgent].text}`} />
                <span className={`font-medium ${agentColors[selectedAgent].text}`}>
                  {agentNames[selectedAgent]}
                </span>
                <ChevronDownIcon className={`h-4 w-4 ${agentColors[selectedAgent].text} transition-transform ${agentDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {agentDropdownOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-48 glass border border-slate-700 rounded-xl shadow-xl overflow-hidden z-50">
                  {['gpt', 'claude', 'gemini'].map(agent => (
                    <button
                      key={agent}
                      type="button"
                      onClick={() => {
                        setSoloAgent(agent);
                        setAgentDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-all ${
                        selectedAgent === agent
                          ? `${agentColors[agent].bg} ${agentColors[agent].text}`
                          : 'text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      <BotIcon className={`h-4 w-4 ${selectedAgent === agent ? agentColors[agent].text : 'text-slate-500'}`} />
                      <span className="font-medium">{agentNames[agent]}</span>
                      {selectedAgent === agent && (
                        <span className="ml-auto text-xs opacity-60">Selected</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Text Input */}
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Ask ${agentNames[selectedAgent]} something...`}
                className="w-full min-h-[48px] px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 transition-all resize-none"
                rows={1}
                disabled={sending || loading}
              />
            </div>

            {/* Send Button */}
            <button
              type="submit"
              disabled={!prompt.trim() || sending || loading}
              className={`h-12 px-6 rounded-xl font-bold transition-all flex items-center gap-2 ${
                prompt.trim() && !sending && !loading
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-lg hover:shadow-cyan-500/30'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              {sending ? (
                <LoaderIcon className="h-5 w-5 animate-spin" />
              ) : (
                <SendIcon className="h-5 w-5" />
              )}
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SoloChat;
